<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ComputedRef } from 'vue'
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/tooltip'
import { useUserStore } from '../authentication/user.store'
import type { LibraryFolder } from './library_types'

interface Props {
    userId: string,
    avatarSize?: 'sm' | 'md' | 'lg'
    folder?: LibraryFolder
}

const props = withDefaults(defineProps<Props>(), {
    userId: '',
    avatarSize: 'md',
    folder: undefined
})

const avatarSize: ComputedRef<string> = computed(() => {
  switch (props.avatarSize) {
    case 'sm':
      return 'w-6 h-6' // 24x24
    case 'md':
      return 'w-7 h-7' // 28x28
    case 'lg':
      return 'w-8 h-8' // 32x32
    default:
      return ''
  }
})

const userStore = useUserStore()
const { getFolderMemberInfo } = storeToRefs(userStore)
const allFolderMembers = computed(() => getFolderMemberInfo.value(props.folder))
const thisFolderMember = computed(() => allFolderMembers.value?.[props.userId] ?? ({
  initials: '?',
  name: 'Unknown user',
  email: '',
  isCurrentUser: false
} as const))
const thisFolderMemberName = computed(() => thisFolderMember.value.name)
const thisFolderMemberEmail = computed(() => thisFolderMember.value.email)
const thisFolderMemberInitials = computed(() => thisFolderMember.value.initials)
const thisFolderMemberIsCurrentUser = computed(() => thisFolderMember.value.isCurrentUser)
const ariaLabel = computed(() => {
    return `${thisFolderMember.value.isOwner ? 'Owner' : 'Contributor'}: ${thisFolderMemberIsCurrentUser.value ? '(Me)' : thisFolderMemberName.value} - Email: ${thisFolderMemberEmail.value}`
})
const stateClasses = computed(() => {
    const classes: string[] = [avatarSize.value]
    if (thisFolderMemberIsCurrentUser.value) {
      classes.push('me')
    } else {
      classes.push('other')
    }
    return classes
})

</script>

<template>
    <TooltipProvider
        :delayDuration="200">
        <Tooltip side="bottom">
        <TooltipTrigger asChild>
            <div
                data-testid="avatar"
                class="rounded-full flex items-center justify-center mr-2 text-sm cursor-help outline-none fs-exclude avatar"
                tabindex="0"
                :aria-label="ariaLabel"
                :class="stateClasses">
                {{ thisFolderMemberInitials }}
            </div>
        </TooltipTrigger>
        <TooltipContent class="max-w-[300px] tooltip-content fs-exclude">
            <p>{{ thisFolderMemberName }} {{ thisFolderMemberIsCurrentUser ? '(Me) ' : '' }}</p>
            <p>{{ thisFolderMemberEmail }}</p>
        </TooltipContent>
        </Tooltip>
    </TooltipProvider>
</template>

<style scoped>
.avatar {
  @apply focus:ring-2 focus:ring-offset-2;

  &.me {
    @apply bg-blue-400 text-white hover:bg-blue-500 focus:bg-blue-500 focus:ring-blue-500;
  }
  &.other {
    @apply text-blue-400 bg-white hover:bg-gray-300 focus:bg-gray-300 focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 border border-blue-400;
  }
}
</style>
